import { get, isFunction } from 'lodash';
import { Check } from 'lucide-react';
import React from 'react';

import { FieldErrors } from '@/types/form-types';
import { tv } from '@/utils/styles';

import { Error } from '../error';

export interface CheckboxProps extends FieldErrors {
  children: React.ReactNode;
  value: string;
  name?: string;
  isSelected?: boolean;
  onChange?: (val: string) => void;
  variant?: 'light' | 'dark';
  className?: string;
  disabled?: boolean;
}

const Checkbox = ({
  children,
  value,
  name = '',
  isSelected = false,
  onChange,
  variant = 'light',
  errors,
  className,
  disabled,
  ...props
}: CheckboxProps) => {
  const fieldError = get(errors, name);

  const checkboxId = `${value}-checkbox`;
  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.stopPropagation();

    if (isFunction(onChange)) {
      onChange(value);
    }
  };

  const { base, input, inputWrapper, inner, label, checkMark, error } = checkBoxStyles({
    variant,
    fieldError: !!fieldError,
    isDisabled: disabled,
  });

  return (
    <div className={base({ className })} {...props}>
      <div className={inner()}>
        <div className={inputWrapper()}>
          <input
            type="checkbox"
            id={checkboxId}
            name={name}
            checked={isSelected}
            onChange={handleOnChange}
            className={input()}
            disabled={disabled}
          />
          <Check size={14} strokeWidth={3} className={checkMark()} />
        </div>
        <label htmlFor={checkboxId} className={label()}>
          {children}
        </label>
      </div>
      {fieldError?.message && <Error className={error()}>{errors[name].message}</Error>}
    </div>
  );
};

const checkBoxStyles = tv({
  slots: {
    base: null,
    label: 'ml-2 inline-block flex-1 cursor-pointer text-baseSm leading-snug',
    inner: 'flex items-start',
    inputWrapper: 'flex items-center justify-center',
    input:
      'peer h-[18px] w-[18px] cursor-pointer appearance-none rounded border-2 border-solid border-grey-300 transition-all duration-200 ease-in-out',
    checkMark: 'pointer-events-none absolute text-transparent peer-checked:text-white',
    error: '-ml-2 mb-2',
  },
  variants: {
    variant: {
      light: {
        base: 'hover:[&_input]:border-lightBlue-400',
        input: 'border-grey-300 bg-white checked:border-lightBlue-500 checked:bg-lightBlue-500',
      },
      dark: {
        base: 'hover:[&_input]:border-lightBlue-300',
        label: 'text-grey-300',
        input: 'border-grey-600 bg-grey-600 checked:border-lightBlue-500 checked:bg-lightBlue-500',
      },
    },
    fieldError: {
      true: {
        input: 'border-forms-states-error shadow-[0_0_0_1px_theme(colors.forms.states.error)]',
        error: 'text-forms-states-error',
      },
    },
    isDisabled: {
      true: {
        base: 'pointer-events-none opacity-50',
      },
    },
  },
  compoundSlots: [
    {
      slots: ['error'],
      fieldError: true,
      variant: 'dark',
      class: 'text-forms-states-errorLight',
    },
  ],
});

export { Checkbox };
